import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";

const navLinks = [
  { label: "Home", href: "/" },
  { label: "About", href: "/about" },
  { label: "Services", href: "/services" },
  { label: "Projects", href: "/urbaninfra" },
  { label: "Gallery", href: "/gallery" },
  { label: "Contact", href: "/contact" },
];

export default function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
      setShowStickyBar(window.scrollY > 300);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const isActive = (href: string) => {
    if (href === "/") return location.pathname === "/";
    return location.pathname.startsWith(href);
  };

  return (
    <>
      {/* Top Bar */}
      <div className="bg-[#1B2A4A] text-white text-xs py-2 px-4 md:px-8 hidden md:block">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-5 flex-wrap">
            <a href="tel:+919945124133" className="flex items-center gap-1.5 hover:text-[#7FBF3F] transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-phone-line text-[#7FBF3F]"></i>+91 99451 24133
            </a>
            <a href="tel:08027822444" className="flex items-center gap-1.5 hover:text-[#7FBF3F] transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-phone-line text-[#7FBF3F]"></i>080 27822444
            </a>
            <a href="mailto:sales@urbaninfraproject.in" className="flex items-center gap-1.5 hover:text-[#7FBF3F] transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-mail-line text-[#7FBF3F]"></i>sales@urbaninfraproject.in
            </a>
          </div>
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1.5 whitespace-nowrap">
              <i className="ri-time-line text-[#7FBF3F]"></i>11:00 AM – 7:00 PM
            </span>
            <a
              href="https://wa.me/919945124133?text=Hi%2C%20I%27m%20interested%20in%20your%20plots.%20Please%20share%20details."
              target="_blank"
              rel="nofollow noreferrer"
              className="flex items-center gap-1.5 bg-[#7FBF3F] hover:bg-[#6aaa32] px-3 py-1 rounded-full transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-whatsapp-line"></i>WhatsApp Us
            </a>
          </div>
        </div>
      </div>

      {/* Sticky Header */}
      <header className={`sticky top-0 z-50 transition-all duration-300 ${scrolled ? "bg-white shadow-md border-b border-gray-100" : "bg-white border-b border-gray-100"}`}>
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <a href="/" className="flex items-center cursor-pointer">
            <img
              src="https://storage.readdy-site.link/project_files/1dbbbcb9-2ec8-461f-ad48-b9a0f216246f/4503346b-1bc6-47f8-8ad2-efa626492725_IMG_1999.png?v=6cac271b56d8e1212d28d915859c81b6"
              alt="Urban Infra Logo"
              className="h-10 md:h-12 w-auto object-contain"
            />
          </a>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-7">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                className={`text-sm font-medium relative group transition-colors cursor-pointer whitespace-nowrap ${isActive(link.href) ? "text-[#7FBF3F] font-semibold" : "text-gray-700 hover:text-[#7FBF3F]"}`}
                style={{ fontFamily: "'Poppins', sans-serif" }}
              >
                {link.label}
                <span className={`absolute -bottom-0.5 left-0 h-0.5 bg-[#7FBF3F] transition-all duration-300 ${isActive(link.href) ? "w-full" : "w-0 group-hover:w-full"}`}></span>
              </a>
            ))}
          </nav>

          {/* CTA + Hamburger */}
          <div className="flex items-center gap-3">
            <a
              href="https://wa.me/919945124133?text=Hi%2C%20I%27m%20interested%20in%20your%20plots.%20Please%20share%20details."
              target="_blank"
              rel="nofollow noreferrer"
              className="hidden lg:inline-flex items-center gap-2 border border-gray-200 hover:border-[#7FBF3F] text-gray-700 hover:text-[#7FBF3F] text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-whatsapp-line text-[#7FBF3F]"></i>WhatsApp
            </a>
            <a
              href="/contact"
              className="hidden lg:inline-flex items-center gap-2 bg-[#7FBF3F] hover:bg-[#6aaa32] text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-calendar-check-line"></i>Book Now
            </a>
            <button
              className="lg:hidden w-10 h-10 flex items-center justify-center text-gray-700 cursor-pointer"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Toggle menu"
            >
              <i className={`text-xl ${menuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {menuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-100 px-4 pb-4">
            <nav className="flex flex-col gap-1 pt-2">
              {navLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={() => setMenuOpen(false)}
                  className={`text-sm font-medium px-3 py-2.5 rounded-lg transition-colors cursor-pointer ${isActive(link.href) ? "text-[#7FBF3F] bg-[#EAF5E1] font-semibold" : "text-gray-700 hover:text-[#7FBF3F] hover:bg-[#EAF5E1]"}`}
                >
                  {link.label}
                </a>
              ))}
              <a
                href="/contact"
                onClick={() => setMenuOpen(false)}
                className="mt-2 flex items-center justify-center gap-2 bg-[#7FBF3F] text-white text-sm font-semibold px-5 py-3 rounded-lg cursor-pointer"
              >
                <i className="ri-calendar-check-line"></i>Book Now
              </a>
              <a
                href="https://wa.me/919945124133?text=Hi%2C%20I%27m%20interested%20in%20your%20plots.%20Please%20share%20details."
                target="_blank"
                rel="nofollow noreferrer"
                className="flex items-center justify-center gap-2 border border-gray-200 text-gray-700 text-sm font-semibold px-5 py-3 rounded-lg cursor-pointer"
              >
                <i className="ri-whatsapp-line text-[#7FBF3F]"></i>WhatsApp Us
              </a>
            </nav>
          </div>
        )}
      </header>

      {/* Mobile Sticky Bottom Bar */}
      <div className={`fixed bottom-0 left-0 right-0 z-40 md:hidden bg-white border-t border-gray-200 transition-transform duration-300 ${showStickyBar ? "translate-y-0" : "translate-y-full"}`}>
        <div className="grid grid-cols-3 divide-x divide-gray-100">
          <a href="tel:+919945124133" className="flex flex-col items-center gap-1 py-3 text-gray-700 hover:bg-[#EAF5E1] transition-colors cursor-pointer">
            <div className="w-6 h-6 flex items-center justify-center">
              <i className="ri-phone-line text-lg text-[#7FBF3F]"></i>
            </div>
            <span className="text-xs font-medium">Call</span>
          </a>
          <a
            href="https://wa.me/919945124133?text=Hi%2C%20I%27m%20interested%20in%20your%20plots.%20Please%20share%20details."
            target="_blank"
            rel="nofollow noreferrer"
            className="flex flex-col items-center gap-1 py-3 bg-[#7FBF3F] text-white hover:bg-[#6aaa32] transition-colors cursor-pointer"
          >
            <div className="w-6 h-6 flex items-center justify-center">
              <i className="ri-whatsapp-line text-lg"></i>
            </div>
            <span className="text-xs font-medium">WhatsApp</span>
          </a>
          <a href="/contact" className="flex flex-col items-center gap-1 py-3 text-gray-700 hover:bg-[#EAF5E1] transition-colors cursor-pointer">
            <div className="w-6 h-6 flex items-center justify-center">
              <i className="ri-calendar-check-line text-lg text-[#7FBF3F]"></i>
            </div>
            <span className="text-xs font-medium">Book Now</span>
          </a>
        </div>
      </div>

      {/* Floating WhatsApp (Desktop) */}
      <a
        href="https://wa.me/919945124133?text=Hi%2C%20I%27m%20interested%20in%20your%20plots.%20Please%20share%20details."
        target="_blank"
        rel="nofollow noreferrer"
        className={`fixed bottom-6 right-6 z-50 hidden md:flex items-center gap-2 bg-[#7FBF3F] hover:bg-[#6aaa32] text-white font-semibold px-5 py-3 rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap ${showStickyBar ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"}`}
      >
        <div className="w-5 h-5 flex items-center justify-center">
          <i className="ri-whatsapp-line text-lg"></i>
        </div>
        WhatsApp Us
      </a>
    </>
  );
}
